import { useEffect, useRef, useState } from "react";
import savingsPlans from "@/services/SavingsPlanService";
import savingsPlansUserIdentities from "@/services/SavingsPlanUserIdentityService";
import userIdentities, { UserIdentityDetailDto } from "@/services/UserIdentitiesService";
import fnmContracts from "@/services/FnmContractService";
import type { IdentitiesSelectionIdentity, PlanOnboardingState } from "@/services/SavingsPlanOnboarding.types";
import { MinorToAdultDataStepKey, findNowAdultOwner, isMinorToAdultDataComplete, isMinorToAdultGateStep } from "@/screen/Map/minorToAdultFlow";

/**
 * 2.2.1 y 2.4 no piden nada al cliente: se avanzan solos hasta 2.4.1. Si un avance falla se devuelve
 * el último estado conocido para que el mapa siga pintándose.
 */
export async function advanceMinorToAdultGateSteps(savingsPlanId: number, state: PlanOnboardingState): Promise<PlanOnboardingState>
{
    let current = state;
    while(isMinorToAdultGateStep(current.currentStep))
    {
        const result = await savingsPlans.advancePlanStep(savingsPlanId, current.currentStep);
        if(!result.success || !result.data)
        {
            console.error(`No se ha podido avanzar el paso ${current.currentStep} del paso a mayoría de edad`, result.errors);
            return current;
        }
        if(result.data.currentStep === current.currentStep)
        {
            return result.data;
        }
        current = result.data;
    }

    return current;
}

export type MinorToAdultHolderStatus = "idle" | "loading" | "pendingData" | "transforming" | "transformed" | "dataIncomplete" | "failed";

export interface MinorToAdultHolderController
{
    status: MinorToAdultHolderStatus;
    adultOwner: IdentitiesSelectionIdentity | null;
}

/**
 * 2.4.1 lo cierra MinorToAdultHolder, no advanceStep. En cuanto los datos del nuevo adulto están completos
 * se lanza desde aquí, que es adonde vuelve el formulario de edición tras guardar.
 */
export function useMinorToAdultHolder(savingsPlanId: number, currentStep: string | null | undefined): MinorToAdultHolderController
{
    const [status, setStatus] = useState<MinorToAdultHolderStatus>("idle");
    const [adultOwner, setAdultOwner] = useState<IdentitiesSelectionIdentity | null>(null);
    const hasStarted = useRef<boolean>(false);

    useEffect(() =>
    {
        if(currentStep !== MinorToAdultDataStepKey || hasStarted.current)
        {
            return;
        }
        hasStarted.current = true;

        const run = async () =>
        {
            setStatus("loading");
            try
            {
                const selection = await savingsPlansUserIdentities.getIdentitiesSelection(savingsPlanId);
                const owner = findNowAdultOwner(selection.data);
                if(owner === null)
                {
                    setStatus("failed");
                    return;
                }
                setAdultOwner(owner);

                const detail = await userIdentities.getUserIdentityDetails(owner.id);
                if(!detail.success || !isMinorToAdultDataComplete(detail.data as UserIdentityDetailDto | null))
                {
                    setStatus("pendingData");
                    return;
                }

                const savingsPlan = await savingsPlans.getSavingsPlan(savingsPlanId);
                const finametrixContract = savingsPlan.fnmContract?.finametrixContract;
                if(!finametrixContract)
                {
                    setStatus("failed");
                    return;
                }

                setStatus("transforming");
                const result = await fnmContracts.minorToAdultHolder(finametrixContract);
                setStatus(result.transformed ? "transformed" : "dataIncomplete");
            }
            catch(error)
            {
                console.error("Error en el paso a mayoría de edad", error);
                setStatus("failed");
            }
        };

        run();
    }, [savingsPlanId, currentStep]);

    return {status, adultOwner};
}
